import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { NextUIProvider } from '@nextui-org/react';
import { ThemeProvider } from './providers/ThemeProvider';

// Landing Page Components
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';

// Dashboard Components
import DashboardLayout from './layouts/DashboardLayout';
import Dashboard from './pages/Dashboard';
import Calendar from './pages/Calendar';
import Profile from './pages/Profile';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import CreateProject from './pages/CreateProject';
import CreateProjectDetails from './pages/CreateProjectDetails';
import CreateProjectCheck from './pages/CreateProjectCheck';
import CreateProjectFinish from './pages/CreateProjectFinish';
import Tasks from './pages/Tasks';
import Organization from './pages/Organization';
import Organizations from './pages/Organizations';
import OrganizationDetail from './pages/OrganizationDetail';
import MindMap from './pages/MindMap';
import Files from './pages/Files';
import Routines from './pages/Routines';
import Reminders from './pages/Reminders';
import Gantt from './pages/Gantt';
import Analytics from './pages/Analytics';
import GoogleCallback from './pages/GoogleCallback';
import Popups from './layouts/popups';
import Premium from './pages/Premium';
import NasilKullanirim from './pages/NasilKullanirim';
import Deck from './pages/Deck';
import QRGenerator from './pages/tools/QRGenerator';
import MailManagement from './pages/MailManagement';
import SocialMedia from './pages/SocialMedia';
import HumanResources from './pages/HumanResources';
import HelpDesk from './pages/HelpDesk';

// Loading Component
const LoadingSpinner = () => (
  <div className="w-full h-screen flex justify-center items-center dark:bg-dark-100">
    <img src="/assets/penalogo.svg" alt="Heypena Logo" width="200" height="200" className="animate-pulse" />
  </div>
);

// Token kontrolü için helper fonksiyon
const isAuthenticated = () => {
  const token = localStorage.getItem('penatoken');
  return token && token !== 'null' && token !== 'undefined';
};

// Auth Guard Component
const ProtectedRoute = ({ children }) => {
  return isAuthenticated() ? children : <Navigate to="/login" />;
};

// Public Route Component (giriş yapmış kullanıcıları dashboard'a yönlendir)
const PublicRoute = ({ children }) => {
  return isAuthenticated() ? <Navigate to="/dashboard" /> : children;
};

function App() {
  return (
    <NextUIProvider>
      <ThemeProvider>
        <div className="font-poppins">
          <Router>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={
                <PublicRoute>
                  <LandingPage />
                </PublicRoute>
              } />
              <Route path="/login" element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              } />
              <Route path="/register" element={
                <PublicRoute>
                  <RegisterPage />
                </PublicRoute>
              } />
              <Route path="/auth/google/callback" element={<GoogleCallback />} />
              
              {/* Protected Routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Dashboard />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/premium" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Premium />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/nasil-kullanirim" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <NasilKullanirim />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/calendar" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Calendar />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/profile" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Profile />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/projects" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Projects />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/projects/:id" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ProjectDetail />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/projects/:id/team" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ProjectDetail />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/projects/:id/timeline" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ProjectDetail />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/projects/:id/tasks" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ProjectDetail />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/projects/:id/kanban" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ProjectDetail />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/projects/:id/financials" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ProjectDetail />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/projects/:id/mind-map" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <MindMap />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/projects/:id/files" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Files />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/projects/:id/routines" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Routines />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/projects/:id/reminders" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Reminders />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/projects/:id/gantt" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Gantt />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/projects/:id/analytics" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Analytics />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/projects/create" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <CreateProject />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/projects/create/details" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <CreateProjectDetails />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/projects/create/check" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <CreateProjectCheck />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/projects/create/finish" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <CreateProjectFinish />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/notes" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Tasks />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/organization" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Organization />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/organizations" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Organizations />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/organizations/:slug" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <OrganizationDetail />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/deck" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Deck />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/tools/qr-generator" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <QRGenerator />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/mail-management" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <MailManagement />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/social-media" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <SocialMedia />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/human-resources" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <HumanResources />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/help-desk" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <HelpDesk />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Suspense>
            </Router>
            <Popups />
          </div>
        </ThemeProvider>
      </NextUIProvider>
  );
}

export default App;
